// File: src/components/LandingVoiceGuide.js
import React, { useState, useEffect, useRef } from 'react';
import { Volume2, Play, Pause, X, Sparkles, RotateCcw } from 'lucide-react';
import {
    unlockLandingAudio,
    playLandingSpeech,
    stopLandingSpeech,
    pauseLandingSpeech,
    resumeLandingSpeech
} from '../utils/landingSpeech';

const BASE = 'https://menu.searchisend.in';

const LandingVoiceGuide = () => {
    const [enabled, setEnabled] = useState(true);
    const [headline, setHeadline] = useState('Grow Restaurant Sales');
    const [dialogHeading, setDialogHeading] = useState('Live Audio Subtitles');
    const [scriptText, setScriptText] = useState('');
    const [scriptSpoken, setScriptSpoken] = useState('');
    const [progress, setProgress] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const [isDismissed, setIsDismissed] = useState(false);
    const scrollRef = useRef(null);

    useEffect(() => {
        if (scrollRef.current) {
            const activeEl = scrollRef.current.querySelector('.active-word');
            if (activeEl) {
                activeEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
    }, [progress]);

    useEffect(() => {
        const fetchScript = async () => {
            try {
                const res = await fetch(`${BASE}/apimenu/landing_actions.php?action=fetch_voice_script`);
                const data = await res.json();
                if (data.success) {
                    setEnabled(data.enabled);
                    if (data.headline) setHeadline(data.headline);
                    if (data.dialog_heading) setDialogHeading(data.dialog_heading);
                    let text = data.script || '';
                    let textSpoken = data.script_spoken || data.script || '';
                    const trialDays = data.trial_days || '7';
                    // Inject dynamic trial days into placeholders
                    text = text.replace(/\{trial_days\}/g, trialDays);
                    textSpoken = textSpoken.replace(/\{trial_days\}/g, trialDays);
                    setScriptText(text);
                    setScriptSpoken(textSpoken);
                }
            } catch (err) {
                console.error("Error fetching landing voice script:", err);
            }
        };
        fetchScript();

        return () => {
            stopLandingSpeech();
        };
    }, []);

    if (!enabled || isDismissed || !scriptText) return null;

    const startAudio = () => {
        playLandingSpeech({
            text: scriptSpoken,
            onStart: () => {
                setIsPlaying(true);
                setIsPaused(false);
                setProgress(0);
            },
            onProgress: (val) => {
                setProgress(val);
            },
            onEnd: () => {
                setIsPlaying(false);
                setIsPaused(false);
                setProgress(0);
            },
            onError: (err) => {
                console.warn("Landing voice guide error:", err);
                setIsPlaying(false);
                setIsPaused(false);
                setProgress(0);
            }
        });
    };

    const handleTogglePlay = (e) => {
        e.stopPropagation();
        unlockLandingAudio();

        if (isPlaying) {
            if (isPaused) {
                resumeLandingSpeech();
                setIsPaused(false);
            } else {
                pauseLandingSpeech();
                setIsPaused(true);
            }
        } else {
            startAudio();
        }
    };

    const handleReplay = (e) => {
        e.stopPropagation();
        setProgress(0);
        startAudio();
    };

    const handleDismiss = (e) => {
        e.stopPropagation();
        stopLandingSpeech();
        setIsPlaying(false);
        setProgress(0);
        setIsDismissed(true);
    };

    return (
        <>
            {/* Bottom Right Floating Audio Badge */}
            <div
                onClick={handleTogglePlay}
                style={{
                    position: 'fixed',
                    bottom: '24px',
                    right: '24px',
                    zIndex: 9999,
                    background: 'var(--lp-bg)',
                    color: 'var(--lp-text)',
                    padding: '10px 18px',
                    borderRadius: '50px',
                    boxShadow: 'var(--lp-prompt-shadow)',
                    border: '1px solid var(--lp-border)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    cursor: 'pointer',
                    fontFamily: "'Outfit', sans-serif",
                    fontSize: '0.9rem',
                    fontWeight: 700,
                    backdropFilter: 'blur(8px)',
                    transition: 'all 0.3s ease'
                }}
            >
                <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: 'var(--lp-orange)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff'
                }}>
                    {isPlaying && !isPaused ? <Pause size={16} /> : <Play size={16} style={{ marginLeft: '2px' }} />}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', textAlign: 'left' }}>
                    <span style={{ fontSize: '0.85rem', color: 'var(--lp-text)', lineHeight: 1.2 }}>
                        {headline} <Sparkles size={12} color="var(--lp-orange)" style={{ display: 'inline', verticalAlign: 'middle' }} />
                    </span>
                    <span style={{ fontSize: '0.7rem', color: 'var(--lp-muted)', fontWeight: 500 }}>
                        {isPlaying ? (isPaused ? 'Paused - Tap to Resume' : 'Playing Audio Guide...') : 'Listen Audio Tip (90s)'}
                    </span>
                </div>

                <button
                    onClick={handleDismiss}
                    style={{
                        background: 'transparent',
                        border: 'none',
                        color: 'var(--lp-muted)',
                        cursor: 'pointer',
                        padding: '2px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginLeft: '4px'
                    }}
                    title="Dismiss"
                >
                    <X size={16} />
                </button>
            </div>

            {/* Subtitles Toast (Visible when playing) */}
            {isPlaying && (
                <div 
                    ref={scrollRef}
                    style={{
                        position: 'fixed',
                    bottom: '80px',
                    right: '24px',
                    maxWidth: '360px',
                    width: 'calc(100vw - 48px)',
                    maxHeight: '260px',
                    zIndex: 9998,
                    background: 'var(--lp-bg)',
                    color: 'var(--lp-text)',
                    borderRadius: '16px',
                    border: '1px solid var(--lp-border)',
                    boxShadow: 'var(--lp-prompt-shadow)',
                    fontSize: '0.95rem',
                    lineHeight: '1.6',
                    fontFamily: "'Outfit', sans-serif",
                    animation: 'fadeIn 0.3s ease',
                    overflowY: 'auto',
                    scrollbarWidth: 'thin',
                    scrollbarColor: 'var(--lp-border) transparent',
                    display: 'flex',
                    flexDirection: 'column'
                }}>
                    <div style={{ position: 'sticky', top: 0, background: 'var(--lp-bg)', padding: '16px 22px', borderBottom: '1px solid var(--lp-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', zIndex: 2 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--lp-orange)', fontSize: '0.8rem', fontWeight: 700, textTransform: 'uppercase' }}>
                            <Volume2 size={16} /> {dialogHeading}
                        </div>
                        <button 
                            onClick={handleReplay}
                            style={{
                                background: 'transparent',
                                border: 'none',
                                color: 'var(--lp-muted)',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                transition: 'color 0.2s ease',
                                padding: '4px 8px',
                                borderRadius: '4px'
                            }}
                            onMouseOver={(e) => e.currentTarget.style.color = 'var(--lp-text)'}
                            onMouseOut={(e) => e.currentTarget.style.color = 'var(--lp-muted)'}
                            title="Replay Audio"
                        >
                            <RotateCcw size={14} /> Replay
                        </button>
                    </div>
                    <div style={{ padding: '14px 22px 22px 22px' }}>
                        {scriptText.split(' ').map((word, idx, arr) => {
                            const isHighlighted = (idx / arr.length) <= progress;
                            const activeWordIndex = Math.floor(progress * arr.length);
                            const isActive = idx === activeWordIndex;
                            return (
                                <span 
                                    key={idx} 
                                    className={isActive ? 'active-word' : ''}
                                    style={{ 
                                        color: isHighlighted ? 'var(--lp-orange)' : 'var(--lp-text)',
                                        opacity: isHighlighted ? 1 : 0.6,
                                    transition: 'color 0.2s ease, opacity 0.2s ease',
                                    marginRight: '0.25rem',
                                    display: 'inline-block'
                                }}>
                                    {word}
                                </span>
                            );
                        })}
                    </div>
                </div>
            )}
        </>
    );
};

export default LandingVoiceGuide;
